import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { fieldNotes } from "@/data/fieldNotes";
import { useRoute } from "wouter";
import NotFound from "@/pages/not-found";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function FieldNoteDetail() {
  const [match, params] = useRoute("/field-notes/:slug");
  
  if (!match || !params?.slug) {
    return <NotFound />;
  }

  const note = fieldNotes.find(n => n.slug === params.slug);
  
  if (!note) {
    return <NotFound />;
  }

  usePageMeta(`${note.title} | Field Notes`, note.excerpt);

  const relatedNotes = fieldNotes
    .filter(n => n.slug !== note.slug)
    .slice(0, 2);

  return (
    <Layout>
      <article className="container mx-auto px-6 max-w-3xl py-12 md:py-20">
        <Link href="/field-notes" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-12 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to all notes
        </Link>
        
        <header className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <time className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {new Date(note.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </time>
            {note.draft && (
              <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border">
                Draft
              </Badge>
            )}
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight mb-8 leading-[1.1]">
            {note.title}
          </h1>
        </header>

        <div className="prose prose-lg md:prose-xl dark:prose-invert max-w-none prose-p:leading-relaxed prose-p:text-muted-foreground">
          {/* Using a simple split by \n\n for placeholder body text. In a real app, this might be MDX or a rich text renderer. */}
          {note.body.split('\n\n').map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
      </article>

      {relatedNotes.length > 0 && (
        <section className="border-t border-border bg-card py-24 mt-20">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-2xl font-serif font-medium mb-10">More Field Notes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedNotes.map(rn => (
                <Link key={rn.slug} href={`/field-notes/${rn.slug}`}>
                  <div className="group p-8 rounded-2xl bg-background border border-border hover:border-accent/30 hover:shadow-sm transition-all cursor-pointer h-full flex flex-col">
                    <h3 className="text-xl font-serif font-medium mb-3 group-hover:text-accent transition-colors">{rn.title}</h3>
                    <p className="text-sm text-muted-foreground mb-6 flex-1 line-clamp-3">{rn.excerpt}</p>
                    <div className="flex items-center text-sm font-medium text-foreground">
                      Read note <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
